// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/PdfCitationPreview.test.tsx
import {
  CaretLeft,
  CaretRight,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
} from "@phosphor-icons/react";
import { Flex, IconButton, Spinner, Text } from "@radix-ui/themes";
import { getDocument, type RenderTask } from "pdfjs-dist";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { ensurePdfjsWorker } from "../../../../../utils/pdfjsWorker";

ensurePdfjsWorker();

/** Page width (CSS px) to render at when the panel has no measured layout yet
 * (jsdom, a display:none ancestor). Fit-to-panel needs a real width; falling
 * back to a page-ish width beats pdf.js's arbitrary default scale. */
const FALLBACK_PANEL_WIDTH = 760;
/** A cited line rendered this tall (CSS px) is comfortably readable. Dense
 * regulatory PDFs set body text at ~8pt, which fits-to-panel at ~10px — legible
 * only if you lean in. The initial zoom targets this instead. */
const TARGET_CITED_LINE_PX = 16;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;

function clampZoom(zoom: number) {
  return Math.min(Math.max(zoom, MIN_ZOOM), MAX_ZOOM);
}

type PdfCitationPreviewProps = {
  /** Raw bytes of the source PDF (from the per-source byte cache). */
  data: ArrayBuffer;
  /** 1-based page the citation points at — the viewer opens here, not page 1. */
  pageNumber: number;
  /** Normalized (0-1, top-left origin) pin position on the cited page. */
  relX?: number;
  relY?: number;
  /** Normalized (0-1) bounding box of the cited text — rendered as a
   * highlight rectangle instead of the center pin when present. */
  bbox?: { x: number; y: number; w: number; h: number } | null;
  /** Testid namespace — override when two previews can coexist on the page
   * (e.g. the inline rule-detail preview under an open viewer dialog), since
   * duplicated testids are Playwright strict-mode violations. */
  testIdPrefix?: string;
};

/**
 * Minimal pdf.js canvas viewer for rule citations, modeled on
 * FilePreviewBlock's cancel-safe render loop: one page at a time, the
 * in-flight RenderTask is cancelled on page change (pdf.js cannot render two
 * pages into one canvas), and a generation counter lets superseded renders
 * no-op. The citation pin renders only on the cited page.
 */
export function PdfCitationPreview({
  data,
  pageNumber,
  relX,
  relY,
  bbox,
  testIdPrefix = "knowledge-source-viewer",
}: PdfCitationPreviewProps) {
  const { t } = useTranslation("ui");
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // The cited line is often lower on the page than the ~70vh viewport shows, so
  // the highlight renders below the fold and reads as "no pin". Scroll it into
  // view once the cited page is painted.
  const highlightRef = useRef<HTMLSpanElement>(null);
  // Fit-to-panel needs the scroll box's own width — the page is rasterized to
  // exactly that many CSS px (times zoom), never rendered wide and squeezed.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(pageNumber);
  const [totalPages, setTotalPages] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const pdfDocRef = useRef<Awaited<
    ReturnType<typeof getDocument>["promise"]
  > | null>(null);
  const renderTaskRef = useRef<RenderTask | null>(null);
  const renderGenRef = useRef(0);
  // bbox arrives as a fresh object literal from the parent on every render, so
  // effects must key off its primitives — not its identity — or they re-run
  // (and re-scroll) on unrelated parent renders.
  const citedLineHeight = bbox?.h ?? 0;
  const citedLineWidth = bbox?.w ?? 0;
  const bboxX = bbox?.x;
  const bboxY = bbox?.y;

  const renderPage = useCallback(async (pageNum: number, pageZoom: number) => {
    const pdfDoc = pdfDocRef.current;
    const canvas = canvasRef.current;
    if (!pdfDoc || !canvas) return;

    const myGen = ++renderGenRef.current;
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel();
      } catch {
        // cancel() may throw if the task already settled; safe to ignore.
      }
      renderTaskRef.current = null;
    }

    setRendering(true);
    try {
      const page = await pdfDoc.getPage(pageNum);
      if (myGen !== renderGenRef.current) return;

      // Rasterize the page to the panel width (times zoom) and give the
      // canvas a backing store of exactly that many DEVICE pixels. Any other
      // ratio makes the browser resample the glyphs: the previous
      // scale-1.5 + `max-w-full` path drew ~1787 device px of page into
      // ~1106 on a Retina panel — a 3.2x non-integer downscale, which is
      // what read as "blurry". why: keep backing == CSS * dpr exactly.
      const unitViewport = page.getViewport({ scale: 1 });
      const panelWidth = scrollRef.current?.clientWidth || FALLBACK_PANEL_WIDTH;
      const fitScale = panelWidth / unitViewport.width;
      const viewport = page.getViewport({ scale: fitScale * pageZoom });
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(viewport.width * dpr);
      canvas.height = Math.round(viewport.height * dpr);
      canvas.style.width = `${Math.round(viewport.width)}px`;
      canvas.style.height = `${Math.round(viewport.height)}px`;

      const context = canvas.getContext("2d");
      if (!context) {
        setError("Failed to render page");
        setRendering(false);
        return;
      }

      const task = page.render({
        canvasContext: context,
        viewport,
        transform: [dpr, 0, 0, dpr, 0, 0],
      });
      renderTaskRef.current = task;
      await task.promise;
      if (myGen === renderGenRef.current) {
        renderTaskRef.current = null;
        setRendering(false);
      }
    } catch (err) {
      if (
        (err as { name?: string } | null)?.name ===
        "RenderingCancelledException"
      ) {
        return;
      }
      if (myGen === renderGenRef.current) {
        setError("Failed to render page");
        setRendering(false);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let loadingTask: ReturnType<typeof getDocument> | null = null;

    async function load() {
      setError(null);
      setRendering(true);
      pdfDocRef.current = null;
      try {
        // pdf.js TRANSFERS the buffer it is given to its worker (neutering
        // the original), so it must get a private copy — handing over the
        // shared cached buffer would detach it and crash the next viewer.
        loadingTask = getDocument({ data: new Uint8Array(data.slice(0)) });
        const pdfDoc = await loadingTask.promise;
        if (cancelled) return;
        pdfDocRef.current = pdfDoc;
        setTotalPages(pdfDoc.numPages);
        const startPage = Math.min(Math.max(1, pageNumber), pdfDoc.numPages);
        setCurrentPage(startPage);
        // Open zoomed far enough in that the cited LINE is readable rather than
        // a hairline: a citation nobody can read points at nothing.
        const startZoom = citedLineZoom(
          await pdfDoc.getPage(startPage),
          { height: citedLineHeight, width: citedLineWidth },
          scrollRef.current?.clientWidth || FALLBACK_PANEL_WIDTH,
        );
        if (cancelled) return;
        setZoom(startZoom);
        await renderPage(startPage, startZoom);
      } catch {
        if (cancelled) return;
        setError("Failed to load PDF");
        setRendering(false);
      }
    }

    load();

    return () => {
      cancelled = true;
      renderGenRef.current++;
      // why the task and not only the document: it frees the parsed document
      // and its worker state, and it also stops a load still in flight, which
      // would otherwise resolve after unmount into a document nothing frees.
      // Each open citation mounts a viewer, so a chat would keep one per open.
      void loadingTask?.destroy().catch(() => undefined);
      pdfDocRef.current = null;
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel();
        } catch {
          // ignore
        }
        renderTaskRef.current = null;
      }
    };
  }, [data, pageNumber, citedLineHeight, citedLineWidth, renderPage]);

  function goToPage(next: number) {
    if (next < 1 || next > totalPages) return;
    setCurrentPage(next);
    renderPage(next, zoom);
  }

  function changeZoom(delta: number) {
    const next = clampZoom(Number((zoom + delta).toFixed(2)));
    if (next === zoom) return;
    setZoom(next);
    renderPage(currentPage, next);
  }

  const onCitedPage = currentPage === pageNumber && !rendering;
  const showBbox = onCitedPage && bbox != null;
  // The bbox rectangle subsumes the center pin — show the pin only when the
  // citation anchored to a point without a box.
  const showPin = onCitedPage && !showBbox && relX != null && relY != null;

  // Once the cited page is painted and the highlight/pin exists, center it in
  // the scroll viewport — otherwise a citation lower on the page opens at the
  // top of the page and the pin sits below the fold (reads as "no pin"). A
  // zoomed page also overflows horizontally, so center both axes.
  useEffect(() => {
    if (!onCitedPage) return;
    highlightRef.current?.scrollIntoView({ block: "center", inline: "center" });
  }, [onCitedPage, currentPage, zoom, bboxX, bboxY, relX, relY]);

  if (error) {
    return (
      <Text size="2" color="red" data-testid={`${testIdPrefix}-error`}>
        {error}
      </Text>
    );
  }

  return (
    <Flex direction="column" gap="2" data-testid={`${testIdPrefix}-pdf`}>
      <Flex gap="2" align="center" justify="center">
        <IconButton
          size="1"
          variant="soft"
          color="gray"
          disabled={currentPage <= 1}
          onClick={() => goToPage(currentPage - 1)}
          aria-label={t("settings.pdfCitationPreview.previousPage")}
          data-testid={`${testIdPrefix}-prev`}
        >
          <CaretLeft aria-hidden="true" />
        </IconButton>
        <Text size="1" data-testid={`${testIdPrefix}-page-indicator`}>
          Page {currentPage}
          {totalPages ? ` of ${totalPages}` : ""}
        </Text>
        <IconButton
          size="1"
          variant="soft"
          color="gray"
          disabled={totalPages === 0 || currentPage >= totalPages}
          onClick={() => goToPage(currentPage + 1)}
          aria-label={t("settings.pdfCitationPreview.nextPage")}
          data-testid={`${testIdPrefix}-next`}
        >
          <CaretRight aria-hidden="true" />
        </IconButton>
        <IconButton
          size="1"
          variant="soft"
          color="gray"
          disabled={zoom <= MIN_ZOOM}
          onClick={() => changeZoom(-ZOOM_STEP)}
          aria-label={t("settings.pdfCitationPreview.zoomOut")}
          data-testid={`${testIdPrefix}-zoom-out`}
        >
          <MagnifyingGlassMinus aria-hidden="true" />
        </IconButton>
        <Text size="1" data-testid={`${testIdPrefix}-zoom-level`}>
          {Math.round(zoom * 100)}%
        </Text>
        <IconButton
          size="1"
          variant="soft"
          color="gray"
          disabled={zoom >= MAX_ZOOM}
          onClick={() => changeZoom(ZOOM_STEP)}
          aria-label={t("settings.pdfCitationPreview.zoomIn")}
          data-testid={`${testIdPrefix}-zoom-in`}
        >
          <MagnifyingGlassPlus aria-hidden="true" />
        </IconButton>
      </Flex>
      <div
        ref={scrollRef}
        className="max-h-[70vh] overflow-auto"
        // why: reserve the vertical scrollbar. Fit-to-panel is measured from
        // this box's clientWidth BEFORE the tall page adds a scrollbar; without
        // a stable gutter the measurement is ~15px too generous and the cited
        // line ends up clipped by exactly that much.
        style={{ scrollbarGutter: "stable" }}
        data-testid={`${testIdPrefix}-scroll`}
      >
        {rendering ? (
          <Flex justify="center" className="py-8">
            <Spinner />
          </Flex>
        ) : null}
        {/* inline-block wrapper hugs the canvas box so percentage overlays
            track the rendered page, not the (possibly wider) scroll area. */}
        <div className="relative inline-block">
          <canvas
            ref={canvasRef}
            // why: `block`, not the default inline. An inline canvas sits on a
            // text baseline, so the inline-block wrapper these percentage
            // overlays are positioned against measures ~3px taller than the
            // page and every highlight drifts down by that fraction.
            className="block"
            aria-label={t("settings.pdfCitationPreview.citedDocumentPage")}
            data-testid={`${testIdPrefix}-canvas`}
          />
          {showBbox ? (
            <span
              ref={highlightRef}
              aria-label={t("settings.pdfCitationPreview.citationHighlight")}
              className="pointer-events-none absolute block rounded-sm"
              // Inline rgba, NOT a Tailwind color class: carrie's palette has no
              // amber-a* scale, so a class-based fill renders invisible
              // (white-on-white). A literal translucent-yellow highlighter over
              // the page + solid amber border cannot depend on the theme.
              style={{
                left: `${bbox.x * 100}%`,
                top: `${bbox.y * 100}%`,
                width: `${bbox.w * 100}%`,
                height: `${bbox.h * 100}%`,
                backgroundColor: "rgba(255, 214, 10, 0.4)",
                border: "2px solid rgba(202, 138, 4, 0.95)",
                boxShadow: "0 0 0 1px rgba(202, 138, 4, 0.35)",
              }}
              data-testid={`${testIdPrefix}-bbox`}
            />
          ) : null}
          {showPin ? (
            <span
              ref={highlightRef}
              aria-label={t("settings.pdfCitationPreview.citationLocation")}
              className="border-white bg-blue-9 absolute block size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-solid shadow-md"
              style={{ left: `${relX * 100}%`, top: `${relY * 100}%` }}
              data-testid={`${testIdPrefix}-pin`}
            />
          ) : null}
        </div>
      </div>
    </Flex>
  );
}

/**
 * Initial zoom that makes the cited line ~TARGET_CITED_LINE_PX tall once the
 * page is fit to the panel, capped so the WHOLE cited line still fits the
 * panel width — zooming past that clips the very text the citation points at
 * behind a horizontal scrollbar. Returns MIN_ZOOM when the citation has no box
 * (no line to size against) or the page has no measurable size.
 */
function citedLineZoom(
  page: {
    getViewport: (opts: { scale: number }) => { width: number; height: number };
  },
  citedLine: { height: number; width: number },
  panelWidth: number,
): number {
  if (!(citedLine.height > 0)) return MIN_ZOOM;
  const unitViewport = page.getViewport({ scale: 1 });
  if (!(unitViewport.width > 0) || !(unitViewport.height > 0)) return MIN_ZOOM;
  const fitScale = panelWidth / unitViewport.width;
  const fittedLinePx = citedLine.height * unitViewport.height * fitScale;
  if (!(fittedLinePx > 0)) return MIN_ZOOM;
  const readableZoom = TARGET_CITED_LINE_PX / fittedLinePx;
  // The line occupies citedLine.width of the page width, so it stops fitting
  // the panel beyond 1 / width. Leave 2% slack so the end of the line is not
  // flush against the panel edge (where centering leaves it half-clipped).
  const widthCap = citedLine.width > 0 ? 0.98 / citedLine.width : MAX_ZOOM;
  // Truncate rather than round: rounding 1.388 up to 1.39 pushes the line a
  // fraction of a pixel past the panel edge, which is what the cap exists to
  // prevent.
  const zoom = Math.floor(Math.min(readableZoom, widthCap) * 100) / 100;
  return clampZoom(zoom);
}
